import React, { useState, useEffect } from "react";
import icon from "../../../Assets/category/Icon.png";
import { PiArrowBendDownRightLight } from "react-icons/pi";
import API from "../../../Utils/api";

// Skeleton for left menu
const MenuSkeleton = () => (
  <div className="flex flex-col gap-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="h-12 rounded-full bg-[#2a4a26] animate-pulse" />
    ))}
  </div>
);

// Skeleton for center image
const ImageSkeleton = () => (
  <div className="bg-[#E7DFC9] rounded-xl flex items-center justify-center col-span-4 min-h-64">
    <div className="w-full h-72 rounded-xl bg-[#d4ccb8] animate-pulse" />
  </div>
);

// Skeleton for right content
const ContentSkeleton = () => (
  <div className="flex flex-col justify-center col-span-3 space-y-4 animate-pulse">
    <div className="h-8 bg-[#2a4a26] rounded w-3/4" />
    <div className="space-y-2">
      <div className="h-3 bg-[#2a4a26] rounded w-full" />
      <div className="h-3 bg-[#2a4a26] rounded w-5/6" />
      <div className="h-3 bg-[#2a4a26] rounded w-4/6" />
    </div>
    <div className="space-y-3 mt-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-3 bg-[#2a4a26] rounded w-2/3" />
      ))}
    </div>
  </div>
);

const Service = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get("/category/all");
      const data = res.data.data || [];
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load services.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const activeCategory = categories[activeIndex];

  return (
    <div className="w-full px-4 sm:px-6 md:px-10 py-4 sm:py-8 bg-yellow-50/30">
      <div className="bg-[#1A3617] rounded-t-2xl p-6 md:p-10">
        {/* Header */}
        <p className="text-[#92AD36] text-sm font-semibold tracking-wide">
          WHAT WE DO
        </p>
        <h2 className="text-2xl md:text-4xl font-serif text-[#EBE4D6] mt-3">
          Services we're offering
        </h2>

        {/* Error State */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-4xl mb-3">😕</span>
            <p className="text-[#EBE4D6] font-medium">{error}</p>
            <button
              onClick={fetchCategories}
              className="mt-4 px-6 py-2 bg-[#92AD36] text-black rounded-full text-sm font-semibold hover:bg-[#a0bc3e] transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && categories.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-4xl mb-3">📂</span>
            <p className="text-[#EBE4D6] font-medium">No services available yet</p>
            <p className="text-[#EBE4D6]/60 text-sm mt-1">Check back soon</p>
          </div>
        )}

        {/* Main Layout */}
        {(loading || categories.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-10 gap-6 mt-10 items-start">

            {/* LEFT MENU */}
            <div className="flex flex-col gap-4 col-span-3">
              {loading ? (
                <MenuSkeleton />
              ) : (
                categories.map((item, index) => (
                  <button
                    key={item._id}
                    onClick={() => setActiveIndex(index)}
                    className={`flex justify-between items-center px-5 py-3 rounded-full border transition-all
                      ${activeIndex === index
                        ? "bg-[#D9D2C3] text-black"
                        : "border-[#4A5D3A] text-white hover:bg-[#1A3617]"
                      }`}
                  >
                    <span className="text-left line-clamp-1">{item.category}</span>
                    <span
                      className={`flex items-center px-3 py-2 rounded-full border transition-all flex-shrink-0
                        ${activeIndex === index
                          ? "bg-[#1A3617] text-white"
                          : "border-[#D9D2C3] bg-[#D9D2C3] text-black"
                        }`}
                    >
                      <PiArrowBendDownRightLight size={15} />
                    </span>
                  </button>
                ))
              )}
            </div>

            {/* CENTER IMAGE */}
            {loading ? (
              <ImageSkeleton />
            ) : (
              <div className="bg-[#E7DFC9] rounded-xl flex items-center justify-center col-span-4 overflow-hidden">
                <img
                  key={activeCategory?._id}
                  src={activeCategory?.image}
                  alt={activeCategory?.category}
                  className="w-full rounded-xl object-cover transition-opacity duration-300"
                />
              </div>
            )}

            {/* RIGHT CONTENT */}
            {loading ? (
              <ContentSkeleton />
            ) : (
              <div className="text-[#EBE4D6] flex flex-col justify-center col-span-3">
                <h3 className="text-xl md:text-3xl mb-8 tracking-wide">
                  {activeCategory?.category}
                </h3>

                <p className="text-[#EBE4D6] text-sm mb-8 tracking-wide leading-relaxed line-clamp-3">
                  {activeCategory?.description || "High quality agricultural product designed to improve crop health and productivity."}
                </p>

                {/* Key Points */}
                <ul className="space-y-4 mb-8">
                  {activeCategory?.keyPoints?.length > 0 ? (
                    activeCategory.keyPoints.map((point, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-xs text-[#EBE4D6B2]"
                      >
                        <span className="w-2 h-2 bg-[#92AD36] rounded-full flex-shrink-0" />
                        {point}
                      </li>
                    ))
                  ) : (
                    // Fallback if no keyPoints
                    <li className="text-xs text-[#EBE4D6B2]">
                      Premium quality assured for all crop types.
                    </li>
                  )}
                </ul>

                <button className="text-[#92AD36] text-start font-semibold hover:underline">
                  Explore more →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrolling Banner — unchanged */}
      <div className="w-full bg-[#E4C50B] overflow-hidden rounded-b-2xl">
        <div className="flex whitespace-nowrap animate-scroll gap-10 py-3 px-4">
          {[...Array(5)].map((_, j) => (
            <React.Fragment key={j}>
              {[
                "Fresh vegetables",
                "Organic product",
                "Agriculture product",
                "Sales of harvest",
                "Dairy products",
                "Fresh fruits",
              ].map((item, i) => (
                <div
                  key={`${j}-${i}`}
                  className="flex items-center gap-2 text-sm font-medium text-black"
                >
                  <img src={icon} alt="icon" />
                  {item}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Service;